/**
 * Directional damage indicator system.
 *
 * Shows red arc/chevron elements around the screen center pointing toward the
 * damage source.  Multiple simultaneous indicators are supported; each has a
 * 1.5 s lifetime and fades out over the final 30%.  Damage severity scales
 * the brightness / thickness of each arc.
 */

const LIFETIME = 1.5; // seconds
const FADE_START = LIFETIME * 0.7; // opacity begins fading at this age
const FADE_DURATION = LIFETIME - FADE_START; // final 30% fade
const RING_RADIUS_PCT = 35; // % of dynamic viewport height for indicator ring
// Responsive arc sizing — `clamp(min, vmin-based, max)` so arcs stay readable
// across desktop and mobile (small phones get bigger relative size).
const ARC_WIDTH_CSS = 'clamp(40px, 8vmin, 80px)';
const ARC_HEIGHT_BASE = 14; // px base (scales with severity)
const MAX_INDICATORS = 8;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface ActiveIndicator {
  el: HTMLElement;
  angle: number; // radians, 0 = front/top, + = clockwise
  age: number;
  intensity: number; // 0.3..1.0
  lifetime: number;
  fadeStart: number;
  fadeDuration: number;
}

export class DamageIndicatorSystem {
  private container: HTMLElement;
  private indicators: ActiveIndicator[] = [];

  constructor() {
    this.container = document.createElement('div');
    this.container.style.cssText = [
      'position: fixed',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'pointer-events: none',
      'z-index: 30',
    ].join('; ');
    document.body.appendChild(this.container);
  }

  // ── Public API ──

  get activeCount(): number {
    return this.indicators.length;
  }

  /**
   * Show a damage indicator pointing toward the source of damage.
   *
   * @param sourceWorldPos  World position of the attacker.
   * @param amount          Damage amount (affects brightness / thickness).
   * @param playerPos       World position of the local player.
   * @param playerYaw       Yaw of the local player in radians (0 = +Z).
   */
  showDamage(sourceWorldPos: Vec3, amount: number, playerPos: Vec3, playerYaw: number, critical = false): void {
    const angle = this.computeAngle(sourceWorldPos, playerPos, playerYaw);
    const intensity = critical ? 1 : this.computeIntensity(amount);
    const lifetime = critical ? 0.85 : LIFETIME;
    const fadeStart = critical ? 0.35 : FADE_START;
    const fadeDuration = lifetime - fadeStart;

    // Evict the oldest if at cap
    if (this.indicators.length >= MAX_INDICATORS) {
      const oldest = this.indicators.shift()!;
      oldest.el.remove();
    }

    const el = this.createArcElement(angle, intensity, critical);
    this.container.appendChild(el);
    this.indicators.push({ el, angle, age: 0, intensity, lifetime, fadeStart, fadeDuration });
  }

  /** Advance time — call once per frame with delta-time in seconds. */
  update(dt: number): void {
    for (let i = this.indicators.length - 1; i >= 0; i--) {
      const ind = this.indicators[i];
      ind.age += dt;

      if (ind.age >= ind.lifetime) {
        ind.el.remove();
        this.indicators.splice(i, 1);
        continue;
      }

      // Compute opacity
      const opacity = this.opacityForAge(ind.age, ind.fadeStart, ind.fadeDuration);
      ind.el.style.opacity = String(opacity);
    }
  }

  /**
   * Get the current opacity of a specific indicator by index.
   * Useful for testing.
   */
  getIndicatorOpacity(index: number): number {
    const ind = this.indicators[index];
    if (!ind) return 0;
    return this.opacityForAge(ind.age, ind.fadeStart, ind.fadeDuration);
  }

  /**
   * Compute the signed angle (radians) from the player's forward to the
   * damage source, projected onto the XZ plane.
   *
   * 0 = directly in front (top of screen).
   * +PI/2 = right.
   * -PI/2 = left.
   * +/-PI = behind.
   */
  computeAngle(sourcePos: Vec3, playerPos: Vec3, playerYaw: number): number {
    const dx = sourcePos.x - playerPos.x;
    const dz = sourcePos.z - playerPos.z;

    // atan2 gives angle of vector from player to source relative to +Z axis
    // In our coordinate system, yaw = 0 means facing +Z
    const worldAngle = Math.atan2(dx, dz); // angle from +Z toward +X

    // Relative angle: difference between world angle and player facing
    let rel = worldAngle - playerYaw;

    // Normalise to (-PI, PI]
    while (rel > Math.PI) rel -= 2 * Math.PI;
    while (rel <= -Math.PI) rel += 2 * Math.PI;

    return rel;
  }

  /**
   * Map damage amount to an intensity value in [0.3, 1.0].
   * 0 damage → 0.3, 100+ damage → 1.0.
   */
  computeIntensity(amount: number): number {
    const t = Math.min(Math.max(amount, 0), 100) / 100;
    return 0.3 + t * 0.7;
  }

  dispose(): void {
    for (const ind of this.indicators) {
      ind.el.remove();
    }
    this.indicators.length = 0;
    this.container.remove();
  }

  // ── Private helpers ──

  private opacityForAge(age: number, fadeStart = FADE_START, fadeDuration = FADE_DURATION): number {
    if (age < fadeStart) return 1;
    const fadeProgress = Math.min(1, Math.max(0, (age - fadeStart) / fadeDuration));
    const eased = 1 - Math.pow(1 - fadeProgress, 3);
    return Math.max(0, 1 - eased);
  }

  private createArcElement(angle: number, intensity: number, critical: boolean): HTMLElement {
    const el = document.createElement('div');

    const height = Math.round(ARC_HEIGHT_BASE * (0.6 + intensity * 0.4));
    const red = Math.round(200 + intensity * 55); // 200..255
    const alpha = (critical ? 0.95 : 0.4 + intensity * 0.4).toFixed(2);
    const baseTransform =
      `translate(-50%, -50%) rotate(${angle}rad) translateY(-${RING_RADIUS_PCT}dvh)`;
    this.ensureCriticalPulseStyle();

    // Position the arc in a ring around screen center.
    // CSS `translate(-50%, -50%)` centres it, then we rotate and offset.
    // Use `dvh` for the ring offset so iOS Safari's dynamic browser chrome
    // (URL bar showing/hiding) doesn't shove indicators off-screen the way
    // static `vh` does.
    el.style.cssText = [
      'position: absolute',
      'top: 50%',
      'left: 50%',
      'pointer-events: none',
      `width: ${ARC_WIDTH_CSS}`,
      `height: ${height}px`,
      'border-radius: 4px',
      `background: radial-gradient(ellipse at center, rgba(${red},0,0,${alpha}), transparent 70%)`,
      `transition: opacity ${critical ? 0.25 : FADE_DURATION}s ease-out`,
      // transform: translate to centre, rotate to point toward source, then push out along that direction
      `--damage-transform: ${baseTransform}`,
      `transform: var(--damage-transform) scale(${critical ? 1.18 : 1})`,
      'transform-origin: center center',
      critical ? 'filter: drop-shadow(0 0 8px rgba(255,0,0,0.95))' : '',
      critical ? 'animation: damage-critical-pulse 0.22s ease-out 0s 2' : '',
    ].join('; ');

    return el;
  }

  private ensureCriticalPulseStyle(): void {
    if (typeof document === 'undefined') return;
    if (typeof document.getElementById === 'function' && document.getElementById('damage-critical-pulse-style')) return;
    if (!document.head || typeof document.head.appendChild !== 'function') return;
    const style = document.createElement('style');
    style.id = 'damage-critical-pulse-style';
    style.textContent =
      '@keyframes damage-critical-pulse {' +
      '0% { transform: var(--damage-transform) scale(1.05); }' +
      '45% { transform: var(--damage-transform) scale(1.48); }' +
      '100% { transform: var(--damage-transform) scale(1.12); }' +
      '}';
    document.head.appendChild(style);
  }
}
